import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatInTz } from "@/lib/timezone";
import {
  TONE_LABEL,
  canRun,
  formatDuration,
  infoLines,
  isOverdue,
  setupMessage,
  summaryLine,
  tone,
} from "@/lib/backupView";

const PILL = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
  running: "bg-amber-50 text-amber-800 border-amber-200",
  never: "bg-amber-50 text-amber-800 border-amber-200",
};

const NOTE = "text-xs px-3 py-2 border bg-amber-50 text-amber-800 border-amber-200";

/**
 * Settings → Backups. Every night each property is copied to a private
 * Cloudflare R2 bucket: its records, its settings and its images. This
 * card shows whether that is working and lets an admin run one now.
 *
 * It also names this installation and holds the nightly switch: the
 * live site and Emergent's preview are two databases sharing one bucket,
 * and the preview should normally be switched off.
 *
 * Restoring is on the Admin page (it must work before any property
 * exists), not here.
 */
export default function BackupsSection({ timezone }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const tz = timezone || "America/Tijuana";

  const { data, refetch } = useQuery({
    queryKey: ["backup-status"],
    queryFn: () => api.getBackupStatus(),
    // Poll while a run is in flight so "running" turns into a result.
    refetchInterval: (q) =>
      q.state.data?.property?.last_status === "running" ||
      q.state.data?.global?.last_status === "running"
        ? 5000
        : false,
  });

  const run = useMutation({
    mutationFn: () => api.runBackupNow(),
    onSuccess: () => {
      toast.success("Backup started — the first one can take a while");
      refetch();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Couldn't start the backup"),
  });

  // Local buffer for the installation name; saved on blur / Enter.
  const serverLabel = data?.installation?.label || "";
  const [label, setLabel] = useState("");
  useEffect(() => {
    setLabel(serverLabel);
  }, [serverLabel]);

  const save = useMutation({
    mutationFn: (patch) => api.saveBackupSettings(patch),
    onSuccess: () => refetch(),
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Couldn't save the backup settings"),
  });

  if (!data) return null;

  const commitLabel = () => {
    const next = label.trim();
    if (next !== serverLabel) save.mutate({ label: next });
  };
  const nightlyOn = data.nightly_enabled !== false;
  const info = infoLines(data.property?.last_summary);

  const prop = data.property || {};
  const glob = data.global || {};
  const t = tone(prop);
  const setup = setupMessage(data);
  const notes = prop.last_summary?.notes || [];
  const runs = data.runs || [];
  const when = (iso) => formatInTz(iso, tz, "compact");

  return (
    <div className="space-y-4" data-testid="backups-section">
      <p className="text-xs text-[var(--ink-muted)] leading-relaxed">
        Every night at {data.time_local} newsroom time, this property&rsquo;s
        articles, research, newsletters, sponsor ads, settings, prompts and
        images are copied to a private storage bucket outside Newsdesk. The
        last {data.retention?.daily} daily and {data.retention?.weekly}{" "}
        weekly copies are kept. Backups include this property&rsquo;s
        WordPress, Facebook and MailerLite connections.
      </p>

      {setup && (
        <div className={NOTE} data-testid="backups-setup">
          {setup}
        </div>
      )}

      {data.configured && isAdmin && (
        <div className="border border-[var(--border)] bg-white p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm" htmlFor="backup-install-label">
              This installation is called
            </label>
            <input
              id="backup-install-label"
              data-testid="backups-install-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              maxLength={40}
              placeholder="Live, Preview…"
              className="border border-[var(--border)] px-2 py-1 text-sm outline-none focus:border-[var(--slate-900)] bg-white w-44"
            />
            <span className="text-[11px] text-[var(--ink-muted)] font-mono-display">
              id {data.installation?.id}
            </span>
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="backups-nightly-toggle"
              checked={nightlyOn}
              disabled={save.isPending}
              onChange={(e) => save.mutate({ nightly_enabled: e.target.checked })}
              className="w-4 h-4 accent-[var(--accent)]"
            />
            Back up every night from this installation
          </label>
          <p className="text-[11px] text-[var(--ink-muted)] leading-relaxed">
            The name is written into every backup so the live site and the
            preview can be told apart in the bucket. The switch covers every
            property on this installation: turn it off in the preview, leave
            it on for live. &ldquo;Back up now&rdquo; works either way.
          </p>
        </div>
      )}

      {data.configured && !nightlyOn && (
        <div className={NOTE} data-testid="backups-nightly-off">
          Nightly backups are switched off on this installation.
        </div>
      )}

      {data.configured && (
        <div className="border border-[var(--border)] bg-white p-4 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                data-testid="backups-pill"
                className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-wider font-mono-display ${PILL[t]}`}
              >
                {TONE_LABEL[t]}
              </span>
              <span className="text-sm">
                {prop.last_success_at
                  ? `Last good backup ${when(prop.last_success_at)}`
                  : "This property has not been backed up yet"}
              </span>
            </div>
            {isAdmin && (
              <button
                type="button"
                data-testid="backups-run-now"
                onClick={() => run.mutate()}
                disabled={!canRun(data, isAdmin, run.isPending)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t === "running" || run.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Archive className="w-3.5 h-3.5" />
                )}
                Back up now
              </button>
            )}
          </div>

          {prop.last_summary && t !== "running" && t !== "error" && (
            <div className="text-xs text-[var(--ink-muted)]" data-testid="backups-summary">
              {summaryLine(prop.last_summary)}
            </div>
          )}

          {t === "error" && prop.last_error && (
            <div className="text-xs px-3 py-2 border bg-red-50 text-red-700 border-red-200">
              {prop.last_error}
            </div>
          )}

          {isOverdue(prop) && (
            <div className={NOTE}>
              The last good backup is more than a day and a half old. The
              nightly run is not completing.
            </div>
          )}

          {t !== "error" &&
            notes.map((n) => (
              <div key={n} className={NOTE}>
                {n}
              </div>
            ))}

          {t !== "error" &&
            info.map((n) => (
              <div key={n} className="text-xs text-[var(--ink-muted)]" data-testid="backups-info">
                {n}
              </div>
            ))}

          <div className="text-[11px] text-[var(--ink-muted)] font-mono-display">
            {prop.next_run_at && nightlyOn ? `Next run ${when(prop.next_run_at)}` : ""}
            {prop.next_run_at && nightlyOn ? " · " : ""}
            Accounts and shared settings:{" "}
            {glob.last_success_at
              ? `backed up ${when(glob.last_success_at)}`
              : "not backed up yet"}
            {data.bucket ? ` · bucket ${data.bucket}` : ""}
          </div>
        </div>
      )}

      {isAdmin && data.configured && (
        <p className="text-[11px] text-[var(--ink-muted)]">
          To check a backup or restore from one, go to{" "}
          <a href="/admin" className="text-[var(--accent)] underline">
            Admin → Restore from backup
          </a>
          .
        </p>
      )}

      {runs.length > 0 && (
        <ul
          data-testid="backups-runs"
          className="divide-y divide-[var(--border)] border border-[var(--border)]"
        >
          {runs.map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap items-center justify-between gap-3 px-3 py-1.5 text-xs"
            >
              <span>
                {when(r.started_at)}
                <span className="text-[var(--ink-muted)]">
                  {" "}
                  · {r.trigger === "manual" ? "manual" : "nightly"}
                  {r.summary
                    ? ` · ${summaryLine(r.summary)} · ${formatDuration(r.summary.duration_s)}`
                    : ""}
                  {r.status === "error" && r.error ? ` · ${r.error}` : ""}
                </span>
              </span>
              <span
                className={`shrink-0 px-1.5 py-0.5 border text-[10px] uppercase tracking-wider font-mono-display ${
                  PILL[r.status] || PILL.never
                }`}
              >
                {r.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
